import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { searchNftsAndUsers } from '../../api/nft/nft';
import { errorMessageHandler } from '../../utils/utils';
import { NFTCard, UserCard, SkeletonCard } from './SearchResultCard';
import LoadMoreSection from '../Common/LoadMoreSection';
import GoForLoginModal from '../Home/GoForLoginModal';
import './SearchResult.css';
import { useCallback } from 'react';

const SearchResult = () => {
    const location = useLocation();
    const history = useHistory();
    const isLoggedIn = useSelector(state => state.Auth.isLoggedIn);

    const [searchText, setSearchText] = useState('');
    const [filter, setFilter] = useState('all');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);

    const [users, setUsers] = useState([]);
    const [nfts, setNfts] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        itemsPerPage: 20,
        totalUsers: 0,
        totalNfts: 0,
        totalResults: 0,
        totalPages: 0
    });


    // Extract search query from URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const query = params.get('q') || '';
        setSearchText(query);
        setCurrentPage(1);
        setUsers([]);
        setNfts([]);

        if (query.trim()) {
            fetchSearchResults(query, 1);
        } else {
            setIsLoading(false);
        }
    }, [location.search]);

    // Fetch search results
    const fetchSearchResults = async (query, page = 1) => {
        try {
            if (page === 1) {
                setIsLoading(true);
            } else {
                setLoadingMore(true);
            }

            const { data } = await searchNftsAndUsers(query, page);

            if (data.Success) {
                const incomingUsers = data.data.results.users || [];
                const incomingNfts = data.data.results.nfts || [];

                // For page 1, replace; for other pages, append
                if (page === 1) {
                    setUsers(incomingUsers);
                    setNfts(incomingNfts);
                } else {
                    setUsers(prev => [...prev, ...incomingUsers]);
                    setNfts(prev => [...prev, ...incomingNfts]);
                }

                setPagination(data.data.pagination || {});
                setError('');
            }

            setIsLoading(false);
            setLoadingMore(false);
        } catch (e) {
            setError(errorMessageHandler(e));
            setIsLoading(false);
            setLoadingMore(false);
        }
    };

    // Load more results
    const loadMore = () => {
        const nextPage = currentPage + 1;
        if (nextPage <= pagination.totalPages) {
            setCurrentPage(nextPage);
            fetchSearchResults(searchText, nextPage);
        }
    };

    // Handle NFT view click with login check
    const handleViewClick = useCallback((item) => {

        if (isLoggedIn) {
            history.push(`/detail/${item.nft_id}`);
        } else {
            setShowLoginModal(true);
        }
    });
    let handleViewClickUser = useCallback((item) => {


        if (isLoggedIn) {
            history.push(`/profile/${item.username}`);
        } else {
            setShowLoginModal(true);
        }
    });

    // Filter results
    const getFilteredResults = () => {
        if (filter === 'nfts') return { users: [], nfts };
        if (filter === 'users') return { users, nfts: [] };
        return { users, nfts };
    };

    const filteredResults = getFilteredResults();
    const hasNextPage = currentPage < pagination.totalPages;

    // Render loading state
    if (isLoading && currentPage === 1) {
        return (
            <main className="MinHeight">
                <div className="search-results-container">
                    <div className="container">
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <p className="loading-text">Searching for "{searchText}"...</p>
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    // Render error state
    if (error) {
        return (
            <main className="MinHeight">
                <div className="search-results-container">
                    <div className="container">
                        <div className="error-state">
                            <div className="error-state-icon">
                                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                            </div>
                            <h2>Search Error</h2>
                            <p>{error}</p>
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    // Render empty state
    if (!searchText.trim()) {
        return (
            <main className="MinHeight">
                <div className="search-results-container">
                    <div className="container">
                        <div className="empty-state">
                            <div className="empty-state-icon">
                                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                </svg>
                            </div>
                            <h2>Start Your Search</h2>
                            <p>Enter a keyword to search for NFTs and users in our marketplace.</p>
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    // Render no results state
    if (pagination.totalResults === 0) {
        return (
            <main className="MinHeight">
                <div className="search-results-container">
                    <div className="container">
                        <div className="search-header">
                            <h1>
                                Search Results for <span className="search-query-text">"{searchText}"</span>
                            </h1>
                        </div>
                        <div className="empty-state">
                            <div className="empty-state-icon">
                                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                    <line x1="8" y1="11" x2="14" y2="11"></line>
                                </svg>
                            </div>
                            <h2>No Results Found</h2>
                            <p>We couldn't find any NFTs or users matching your search. Try different keywords.</p>
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="MinHeight">
            <div className="search-results-container">
                <div className="container">
                    {/* Search Header */}
                    <div className="search-header">
                        <h1>
                            Search Results for <span className="search-query-text">"{searchText}"</span>
                        </h1>
                        <div className="search-stats">
                            <div className="stat-item">
                                <span className="stat-label">Total Results</span>
                                <span className="stat-value">{pagination.totalResults}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">NFTs</span>
                                <span className="stat-value">{pagination.totalNfts}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Users</span>
                                <span className="stat-value">{pagination.totalUsers}</span>
                            </div>
                        </div>
                    </div>

                    {/* Filter Tabs */}
                    <div className="filter-tabs">
                        <button
                            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            <span>All Results</span>
                            <span className="filter-tab-count">{pagination.totalResults}</span>
                        </button>
                        <button
                            className={`filter-tab ${filter === 'nfts' ? 'active' : ''}`}
                            onClick={() => setFilter('nfts')}
                        >
                            <span>NFTs</span>
                            <span className="filter-tab-count">{pagination.totalNfts}</span>
                        </button>
                        <button
                            className={`filter-tab ${filter === 'users' ? 'active' : ''}`}
                            onClick={() => setFilter('users')}
                        >
                            <span>Users</span>
                            <span className="filter-tab-count">{pagination.totalUsers}</span>
                        </button>
                    </div>

                    {/* NFT Results */}
                    {(filter === 'all' || filter === 'nfts') && filteredResults.nfts.length > 0 && (
                        <div className="results-section">
                            <div className="section-header">
                                <h2 className="section-title">NFTs</h2>
                                <span className="section-count">{pagination.totalNfts} found</span>
                            </div>
                            <div className="results-grid">
                                {filteredResults.nfts.map((nft, index) => (
                                    <NFTCard
                                        key={`${nft.nft_id}-${index}`}
                                        nft={nft}
                                        handleViewClick={handleViewClick}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* User Results */}
                    {(filter === 'all' || filter === 'users') && filteredResults.users.length > 0 && (
                        <div className="results-section">
                            <div className="section-header">
                                <h2 className="section-title">Users</h2>
                                <span className="section-count">{pagination.totalUsers} found</span>
                            </div>
                            <div className="results-grid">
                                {filteredResults.users.map((user, index) => (
                                    <UserCard key={`${user.user_id}-${index}`} user={user} handleViewClick={handleViewClickUser} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Load More Section */}
                    {(filter === 'all' && (filteredResults.users.length > 0 || filteredResults.nfts.length > 0)) && (
                        <LoadMoreSection
                            hasNextPage={hasNextPage}
                            onLoadMore={loadMore}
                            isLoading={loadingMore}
                            loadMoreText="Load More Results"
                            endMessage="You've reached the end of search results!"
                            accentColor="17, 207, 243"
                            compact={false}
                        />
                    )}
                </div>
            </div>

            {/* Login Modal */}
            <GoForLoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                onSuccess={() => {
                    setShowLoginModal(false);
                    history.push("/login");
                }}
            />
        </main>
    );
};

export default SearchResult;
